import { useNavigate } from "react-router-dom";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { Button } from "primereact/button";
import { FormikProvider, useFormik } from "formik";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import { ProtocolResponse } from "../../models/dtos";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { teachersApi } from "../../services/teachers/teachersApi";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";
import { sessionTypesApi } from "../../services/nomenclatures/sessionTypesApi";
import { examTypesApi } from "../../services/nomenclatures/examTypesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { semesterTypesApi } from "../../services/nomenclatures/semesterTypesApi";
import { subjectsApi } from "../../services/nomenclatures/subjectsApi";
import Error from "../../components/common/Error";
import { ProgressSpinner } from "primereact/progressspinner";
import ProtocolMainFields from "./ProtocolMainFields";

const ProtocolCreate = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [updateProtocol, { isLoading: isUpdateLoading, error: updateError }] = protocolsApi.useUpdateProtocolMutation();
  const [checkDuplicateProtocol, { isLoading: isCheckingDuplicate }] = protocolsApi.useCheckDuplicateProtocolMutation();

  const { data: teachers } = teachersApi.useGetTeachersQuery({});
  const { data: specialities } = specialitiesApi.useGetSpecialitiesAsFlatListQuery();
  const { data: sessionTypes } = sessionTypesApi.useGetSessionTypesQuery();
  const { data: examTypes } = examTypesApi.useGetExamTypesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const { data: semesterTypes } = semesterTypesApi.useGetSemesterTypesQuery();

  const [triggerGetSubjectsBySpecialityIdQuery] = subjectsApi.useLazyGetSubjectsBySpecialityIdQuery();
  const [triggerGetSubjectsByFacultyOfSpecialityQuery] = subjectsApi.useLazyGetSubjectsByFacultyOfSpecialityQuery();

  const [useSubjectsByFaculty, setUseSubjectsByFaculty] = useState<boolean>(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateProtocol, setDuplicateProtocol] = useState<ProtocolResponse | null>(null);

  const emptyInitialValues: Partial<ProtocolResponse> = {
    protocolId: 0,
    protocolNumber: "",
    teacherId: null,
    substituteTeacherId: null,
    specialityId: 0,
    subjectId: 0,
    issueDate: undefined,
    sessionTypeId: 0,
    examTypeId: 0,
    examDate: null,
    secondExamDate: null,
    academicYearId: 0,
    semesterTypeId: 0,
    semester: null,
  };

  const formik = useFormik<Partial<ProtocolResponse>>({
    initialValues: emptyInitialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const duplicateCheck = await checkDuplicateProtocol({
          subjectId: values.subjectId!,
          specialityId: values.specialityId!,
          semester: values.semester ?? 0,
          academicYearId: values.academicYearId!,
          sessionTypeId: values.sessionTypeId!,
          protocolId: 0,
        }).unwrap();

        if (duplicateCheck?.data) {
          setDuplicateProtocol(duplicateCheck.data);
          setShowDuplicateDialog(true);
          return;
        }
      } catch (error) {
        console.error("Error checking for duplicate protocol:", error);
      }

      updateProtocol(values)
        .unwrap()
        .then((result) => {
          if (result && "data" in result && result.data) {
            navigate(`/protocols/${result.data}`);
          }
        });
    },
  });

  const lookupsLoading =
    !teachers?.data ||
    !specialities?.data ||
    !sessionTypes?.data ||
    !examTypes?.data ||
    !academicYears?.data ||
    !semesterTypes?.data;

  return (
    <FormikProvider value={formik}>
      <div>
        <Toast ref={toast} />

        {(isUpdateLoading || isCheckingDuplicate || lookupsLoading) && <ProgressSpinner />}
        {updateError && <Error error={updateError} />}

        {!lookupsLoading && (
          <form onSubmit={formik.handleSubmit}>
            <Card
              title="Нов протокол"
              footer={<Button type="submit" label="Запази" icon="pi pi-save" />}
              className="mb-4 shadow-4"
              pt={{
                footer: {
                  className: "flex justify-content-center",
                },
              }}
            >
              <ProtocolMainFields
                teachers={teachers!.data || []}
                substituteTeachers={teachers!.data || []}
                specialities={specialities!.data || []}
                sessionTypes={sessionTypes!.data || []}
                examTypes={examTypes!.data || []}
                academicYears={academicYears!.data || []}
                semesterTypes={semesterTypes!.data || []}
                useSubjectsByFaculty={useSubjectsByFaculty}
                setUseSubjectsByFaculty={setUseSubjectsByFaculty}
                loadSubjects={(specialityId) =>
                  useSubjectsByFaculty
                    ? triggerGetSubjectsByFacultyOfSpecialityQuery(specialityId)
                    : triggerGetSubjectsBySpecialityIdQuery(specialityId)
                }
                subjectLabel={undefined}
              />
            </Card>
          </form>
        )}

        <Dialog
          header="Дублиран протокол"
          visible={showDuplicateDialog}
          onHide={() => setShowDuplicateDialog(false)}
          style={{ width: "50vw" }}
        >
          <div>
            <p>Протокол с тези параметри вече съществува:</p>
            {duplicateProtocol && (
              <div className="mt-3">
                <div className="grid">
                  <div className="col-12">
                    <strong>Номер на протокол:</strong> {duplicateProtocol.protocolNumber}
                  </div>
                  <div className="col-12">
                    <strong>Дисциплина:</strong> {duplicateProtocol.subjectName}
                  </div>
                  <div className="col-12">
                    <strong>Специалност:</strong> {duplicateProtocol.specialityName}
                  </div>
                  <div className="col-12">
                    <strong>Семестър:</strong> {duplicateProtocol.semester}
                  </div>
                  <div className="col-12">
                    <strong>Академична година:</strong> {duplicateProtocol.academicYearName}
                  </div>
                  <div className="col-12">
                    <strong>Сесия:</strong> {duplicateProtocol.sessionTypeName}
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Отиди към съществуващия протокол"
                onClick={() => {
                  if (duplicateProtocol) {
                    setShowDuplicateDialog(false);
                    setDuplicateProtocol(null);
                    navigate(`/protocols/${duplicateProtocol.protocolId}`);
                  }
                }}
              />
              <Button
                label="Създай нов протокол"
                severity="secondary"
                onClick={async () => {
                  setShowDuplicateDialog(false);
                  setDuplicateProtocol(null);
                  const values = formik.values;
                  updateProtocol(values)
                    .unwrap()
                    .then((result) => {
                      if (result && "data" in result && result.data) {
                        navigate(`/protocols/${result.data}`);
                      }
                    });
                }}
              />
            </div>
          </div>
        </Dialog>
      </div>
    </FormikProvider>
  );
};

export default ProtocolCreate;
